import { Link } from 'react-router-dom';
import type { ResolvedSlot } from '@/pages/vacation-timetable/utils';
import { DAY_NAMES, formatTime } from '@/lib/constants';

type Props = {
  items: ResolvedSlot[];
  showTeacher: boolean;
  onEdit: (item: ResolvedSlot) => void;
};

export default function VacationWeeklyTimetable({ items, showTeacher, onEdit }: Props) {
  const days = [1, 2, 3, 4, 5, 6, 0];

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7">
      {days.map((day) => {
        const dayItems = items
          .filter((i) => i.dayOfWeek === day)
          .sort((a, b) => a.startTime.localeCompare(b.startTime));
        const isToday = new Date().getDay() === day;

        return (
          <div
            key={day}
            className={`rounded-lg border bg-white ${
              isToday ? 'border-accent-300' : 'border-background-200'
            }`}
          >
            <div
              className={`rounded-t-lg px-3 py-2 text-center text-sm font-bold ${
                isToday
                  ? 'bg-accent-500 text-background-50'
                  : 'bg-background-50 text-foreground-700'
              }`}
            >
              {DAY_NAMES[day]}요일
              {isToday && <span className="ml-1 text-[10px] font-normal">오늘</span>}
            </div>
            <div className="space-y-2 p-2">
              {dayItems.length === 0 ? (
                <p className="py-6 text-center text-xs text-foreground-300">-</p>
              ) : (
                dayItems.map((item) => {
                  const classId = item.raw.class_id;
                  const body = (
                    <>
                      <p className="truncate pr-6 text-sm font-semibold text-foreground-900">
                        {item.name}
                      </p>
                      {item.subject && (
                        <p className="truncate text-xs text-foreground-500">{item.subject}</p>
                      )}
                      {showTeacher && (
                        <p className="mt-1 flex items-center gap-1 truncate text-[11px] text-secondary-700">
                          <i className="ri-user-line"></i>
                          {item.teacherName}
                        </p>
                      )}
                      <p className="mt-1 text-xs font-medium text-accent-700">
                        {formatTime(item.startTime)} ~ {formatTime(item.endTime)}
                      </p>
                      {item.students.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1 border-t border-accent-100 pt-1.5">
                          {item.students.map((name) => (
                            <span
                              key={name}
                              className="rounded-full bg-white px-2 py-0.5 text-xs font-medium leading-5 text-foreground-700"
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                      )}
                    </>
                  );
                  return (
                    <div
                      key={item.id}
                      className="group relative rounded-md border border-accent-200 bg-accent-50/70 p-2.5 transition-colors hover:bg-accent-100/70"
                    >
                      <button
                        onClick={() => onEdit(item)}
                        title="수업 편집"
                        className="absolute right-1.5 top-1.5 z-10 flex h-6 w-6 cursor-pointer items-center justify-center rounded-md text-foreground-400 opacity-0 transition-opacity hover:bg-white hover:text-foreground-900 group-hover:opacity-100"
                      >
                        <i className="ri-pencil-line text-xs"></i>
                      </button>
                      {classId ? (
                        <Link
                          to={`/classes/${classId}`}
                          state={{ from: 'vacation-timetable' }}
                          onClick={() =>
                            sessionStorage.setItem('classDetailFrom', 'vacation-timetable')
                          }
                          className="block cursor-pointer"
                        >
                          {body}
                        </Link>
                      ) : (
                        <div onClick={() => onEdit(item)} className="cursor-pointer">
                          {body}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}